import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/utils/shadcn";
import { BRAND, COIN, LAB } from "./tokens";

/* ── Lab · the mono uppercase micro-label. The house voice. ─────────── */

export const Lab = ({
  children,
  className,
  size = 10,
  style,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  /** px — the mock uses 10 in body copy, 9 for sub-lines, 9.5 in heads */
  size?: number;
  style?: React.CSSProperties;
  title?: string;
}) => (
  <span
    title={title}
    className={cn(LAB, "text-ms-mute", className)}
    style={{ fontSize: size, ...style }}
  >
    {children}
  </span>
);

/* ── Tag · tinted mono badge. The only place colour carries meaning. ── */

export type TagTone = "pos" | "warn" | "neu" | "neg" | "live";

const TAG_TONE: Record<TagTone, string> = {
  pos: "text-ms-link bg-ms-mint-soft",
  warn: "text-ms-tan bg-ms-tan-soft",
  neu: "text-ms-mute bg-ms-neu-soft",
  neg: "text-ms-neg bg-ms-neg-soft",
  live: "text-ms-rule bg-ms-mint-soft",
};

export const Tag = ({
  tone = "neu",
  children,
  className,
}: {
  tone?: TagTone;
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      LAB,
      "inline-flex items-center gap-1 whitespace-nowrap rounded-md px-[7px] py-1 text-[9.5px]",
      TAG_TONE[tone],
      className
    )}
  >
    {children}
  </span>
);

/* ── Coin + brand marks ─────────────────────────────────────────────── */

export const CoinMark = ({
  sym,
  size = 30,
  className,
}: {
  sym: string;
  size?: number;
  className?: string;
}) => {
  const key = (sym || "").toUpperCase();
  const c = COIN[key];
  return (
    <span
      className={cn(
        "inline-grid flex-none place-items-center rounded-full font-mono font-semibold",
        !c && "bg-ms-chip text-ms-mute",
        className
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.37),
        background: c || undefined,
        color: c ? "#fff" : undefined,
      }}
    >
      {key.slice(0, 3)}
    </span>
  );
};

export const BrandMark = ({
  brand,
  size = 28,
  className,
}: {
  /** exchange/wallet slug — falls back to a neutral chip when unknown */
  brand: string;
  size?: number;
  className?: string;
}) => {
  const b = BRAND[(brand || "").toLowerCase()];
  return (
    <span
      className={cn(
        "inline-grid flex-none place-items-center rounded-lg font-mono font-bold",
        !b && "bg-ms-chip text-ms-mute",
        className
      )}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.29),
        fontSize: Math.round(size * 0.4),
        background: b ? b[0] : undefined,
        color: b ? b[2] : undefined,
      }}
    >
      {b ? b[1] : (brand || "?").slice(0, 1).toUpperCase()}
    </span>
  );
};

/* ── Pill · the commit CTA. Green in both themes, ink flips. ────────── */

type PillProps<T extends React.ElementType> = {
  as?: T;
  variant?: "solid" | "ghost" | "off";
  className?: string;
  children: React.ReactNode;
};

const PILL_VARIANT = {
  solid:
    "bg-ms-pill-bg text-ms-pill-ink hover:bg-ms-pill-bg-hover border border-transparent",
  ghost:
    "bg-ms-card text-ms-ink border border-ms-line2 hover:border-ms-ink",
  off: "bg-ms-card2 text-ms-dim border border-ms-line cursor-default",
};

export function Pill<T extends React.ElementType = "button">({
  as,
  variant = "solid",
  className,
  children,
  ...rest
}: PillProps<T> & Omit<React.ComponentPropsWithoutRef<T>, keyof PillProps<T>>) {
  const Comp: any = as || "button";
  return (
    <Comp
      {...(Comp === "button" ? { type: "button" } : {})}
      {...rest}
      className={cn(
        // "ms-pill" lets container-level `a` styling (e.g. Callout's link ink) exclude pills
        "ms-pill inline-flex min-h-[50px] items-center justify-center gap-2.5 rounded-full px-[22px] text-[15px] font-semibold tracking-[-0.02em] transition-colors",
        PILL_VARIANT[variant],
        className
      )}
    >
      {children}
    </Comp>
  );
}

/* ── Band · the status strip that sits under the header on every screen ─ */

export const Band = ({
  left,
  right,
  className,
}: {
  left: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-center justify-between gap-3 border-b border-ms-line px-[var(--pad)] py-3",
      className
    )}
  >
    <span className="flex min-w-0 items-center gap-2.5">{left}</span>
    {right ? <span className="flex flex-none items-center gap-2.5">{right}</span> : null}
  </div>
);

/* ── Crumbs · the ancestry line. First child inside <Screen>, above the heading. ── */

export type Crumb = { label: string; to?: string };

export const Crumbs = ({
  items,
  className,
}: {
  items: Crumb[];
  className?: string;
}) => (
  <nav
    aria-label="Breadcrumb"
    className={cn(LAB, "flex flex-wrap items-center text-[10px] text-ms-mute", className)}
  >
    {items.map((item, i) => {
      const last = i === items.length - 1;
      return (
        <React.Fragment key={`${item.label}-${i}`}>
          {i > 0 && (
            <span className="px-1.5 text-ms-dim" aria-hidden="true">
              /
            </span>
          )}
          {item.to && !last ? (
            <Link to={item.to} className="transition-colors hover:text-ms-ink">
              {item.label}
            </Link>
          ) : (
            <span className={last ? "text-ms-ink" : undefined} aria-current={last ? "page" : undefined}>
              {item.label}
            </span>
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

/* Callout (the alert box) lives in ./alerts alongside AlertBar. */

/* ── Foot · the page footer line ────────────────────────────────────── */

export const Foot = ({
  children,
  right,
  className,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-start gap-3.5 border-t border-ms-line px-[var(--pad)] pb-7 pt-3.5 text-[10px] leading-[1.45] text-ms-dim",
      className
    )}
  >
    <span className="flex-1">{children}</span>
    {right ? <Lab className="flex-none">{right}</Lab> : null}
  </div>
);

/* ── tiny svgs the kit reuses ───────────────────────────────────────── */

export const Chevron = ({ size = 9, className }: { size?: number; className?: string }) => (
  <svg width={size} height={(size * 6) / 10} viewBox="0 0 10 6" fill="none" className={className}>
    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const CheckMark = ({ size = 12, className }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" className={className}>
    <path d="M1.5 6.3l3 3L10.5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Padlock = ({ size = 11, className }: { size?: number; className?: string }) => (
  <svg width={size} height={(size * 15) / 14} viewBox="0 0 14 15" fill="none" className={className}>
    <rect x="2" y="6.5" width="10" height="7.5" rx="1.8" stroke="currentColor" strokeWidth="1.3" />
    <path d="M4.6 6.5V4.4A2.6 2.6 0 019.8 4.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
